import React from 'react';
import { Link } from 'react-router-dom';
import { EmailWhatsAppActions, WhatsAppButton } from './ContactActions';
import HeroVideo from './HeroVideo';
import {
  LEAD_PACKAGE_CHATBOT_SERVICE_NOTE,
  LEAD_PACKAGE_TITLE,
} from '../constants/leadPackage';
import { GET_QUOTE_HREF, GET_QUOTE_LABEL } from '../constants/quote';
import { WHATSAPP_DEFAULT_MESSAGE, buildWhatsAppUrl } from '../constants/whatsapp';

const offers = [
  {
    title: LEAD_PACKAGE_TITLE,
    leadPackage: true,
    quoteHref: buildWhatsAppUrl(WHATSAPP_DEFAULT_MESSAGE),
    unit: 'landing page + WhatsApp chatbot setup',
    description:
      'Our lead package for small businesses: a template-based landing page plus WhatsApp chatbot setup so customers can learn about you and start a conversation 24/7.',
    includes: [
      'Responsive landing page with your services, details, and supplied images',
      'WhatsApp chatbot wired to your FAQs and enquiry flow',
      'Handoff rules so a person takes over when needed',
    ],
    note: `${LEAD_PACKAGE_CHATBOT_SERVICE_NOTE} Standalone landing pages without a chatbot are also available — ask if you only need the page.`,
    action: 'Enquire about this package',
    subject: 'Landing page + WhatsApp chatbot package',
    body:
      'Hi RosuePro,\n\nI am interested in the landing page + WhatsApp chatbot package.\nBusiness name and services: \nMain questions for the chatbot: \nDo you have text, logo, and images ready? \nPreferred deadline: ',
    whatsappMessage: WHATSAPP_DEFAULT_MESSAGE,
  },
  {
    title: 'Custom websites',
    quoteHref: buildWhatsAppUrl(
      'Hi RosuePro,\n\nI would like a custom website for my business.\nBusiness name and what you do: \nPages or sections you need: \nDo you have logo, text, and images ready? \nPreferred deadline: '
    ),
    unit: 'for an agreed business website scope',
    description:
      'A website built around your business—your services, branding, and contact details—so people can find you on Google, learn what you offer, and reach out when they are ready. Showing up online helps more customers discover you and can lead to more sales for your business.',
    includes: [
      'Mobile-friendly pages tailored to your business and supplied content',
      'Clear service or product information visitors and search engines can read',
      'Contact details and enquiry paths so interested customers can reach you',
    ],
    note: 'Scope, number of pages, and final quote are confirmed before build. Domain, hosting renewal, and ongoing content updates are quoted separately unless agreed upfront.',
    action: 'Enquire about a custom website',
    subject: 'Custom website enquiry',
    body:
      'Hi RosuePro,\n\nI would like a custom website for my business.\nBusiness name and what you do: \nPages or sections you need: \nDo you have logo, text, and images ready? \nPreferred deadline: ',
  },
  {
    title: 'Google Business Profile (Google Maps)',
    quoteHref: GET_QUOTE_HREF,
    unit: 'scope confirmed before we begin',
    description:
      'Claim and verify your business on Google Maps, then set up and customise your Google Business Profile so people can find you and contact you. Rosue walks business owners who own the business through finding or creating the listing, requesting ownership, and completing Google’s verification (you do the check yourself—for example video, phone, or postcard). After it is claimed, we help you shape the listing with your description, categories, hours, and the details customers look for.',
    includes: [
      'Find your existing Google Maps listing or create one, then request ownership and complete Google’s verification (you complete the check)',
      'Customise your profile: business description, categories, hours, services or products, photos and logo, and attributes',
      'Add phone, WhatsApp, and a website link—and connect a RosuePro website when you have one',
    ],
    note:
      'You must own the business (or be authorised to manage it). RosuePro never claims a listing for someone who does not own the business. Google decides verification outcomes; we do not guarantee approval, rankings, traffic, or how many people will find you.',
    action: 'Ask about Google Business Profile',
    subject: 'Google Business Profile (Google Maps) enquiry',
    body:
      'Hi RosuePro,\n\nI own this business and would like help with my Google Business Profile on Google Maps.\nBusiness name: \nLocation or area served: \nExisting listing on Google Maps (yes / no / not sure): \nRosuePro website to link (if any): \nPreferred deadline: ',
  },
  {
    title: 'Promo videos',
    playBadge: true,
    quoteHref: GET_QUOTE_HREF,
    unit: 'scope confirmed before we begin',
    description:
      'Short, eye-catching videos that show off your business, offer, or website, made for WhatsApp Status, Instagram and Facebook Reels, and your Facebook feed.',
    includes: [
      'Vertical 9:16 for Reels, Stories and WhatsApp Status, plus a 16:9 cut for the feed and your website',
      'Your offer, logo, and contact details on screen, with burned-in captions that work without sound',
      'Background music and a clear call to action (WhatsApp or website)',
    ],
    note: 'You supply the details, photos, or screen recordings. Length, number of versions, and quote are confirmed before we start.',
    action: 'Request a video quote',
    subject: 'Promo video quote',
    body: 'Hi RosuePro,\n\nI would like a promo video for my business.\nBusiness name and what to promote: \nWhere it will be posted (WhatsApp Status, Reels, Facebook, website): \nDo you have photos, logo, or screen recordings ready? \nPreferred deadline: ',
  },
  {
    title: 'AI automations',
    quoteHref: GET_QUOTE_HREF,
    unit: 'per agreed workflow or integration',
    description: 'Reduce repetitive admin with a focused automation connected to the tools your business already uses.',
    includes: ['One business process mapped and automated (e.g. intake, follow-ups, reminders, data entry)', 'Connections between email, forms, spreadsheets, or your website where needed', 'Testing, simple documentation, and a walkthrough when it goes live'],
    note: 'Tell us what you do manually today. Scope, tools, and quote are confirmed before build; hosting fees and ongoing changes are quoted separately.',
    action: 'Discuss an automation',
    subject: 'AI automation enquiry',
    body: 'Hi RosuePro,\n\nI would like help automating a task.\nWhat happens today (step by step): \nTools involved (email, WhatsApp, website, Excel, etc.): \nHow often: \nPreferred deadline: ',
  },
  {
    title: 'AI chatbots',
    quoteHref: GET_QUOTE_HREF,
    unit: 'for a scoped website or WhatsApp bot',
    description: 'Handle common customer questions, capture leads, or support staff—with clear rules on when a person takes over.',
    includes: ['Answers based on your FAQs and business details', 'Lead capture or handoff to email or WhatsApp', 'Setup for a website chat widget or WhatsApp Business (where supported)'],
    note: 'You supply FAQs, tone, and when to escalate to a human. Extra languages, deep integrations, or high-volume support are quoted separately.',
    action: 'Request a chatbot quote',
    subject: 'AI chatbot enquiry',
    body: 'Hi RosuePro,\n\nI would like a chatbot.\nBusiness and main questions it should answer: \nWebsite, WhatsApp, or both: \nWhen should it hand off to a person? \nPreferred deadline: ',
  },
];

function QuoteLink({ href, className = 'service-price mt-3 mb-0 d-inline-block' }) {
  return (
    <a className={className} href={href} target="_blank" rel="noopener noreferrer">
      {GET_QUOTE_LABEL}
    </a>
  );
}

export default function ServiceOfferings() {
  return (
    <section className="service-offerings pb-5" id="services" aria-labelledby="services-heading">
      <HeroVideo>
            <div className="services-intro mx-auto text-center mb-4">
              <p className="service-eyebrow">ROSUEPRO · LANDING PAGES &amp; AI CHATBOTS</p>
              <h1 id="services-heading">
                Landing pages and AI chatbots that turn visitors into customers
              </h1>
              <p className="lead mt-3">
                RosuePro builds fast, clean websites with a built-in WhatsApp chatbot for small businesses anywhere. Remote, start to finish.
              </p>
              <p className="small text-secondary mb-0">{LEAD_PACKAGE_CHATBOT_SERVICE_NOTE}</p>
              <p className="service-availability mt-3">Message us anytime on WhatsApp or email</p>
              <div className="contact-actions d-flex flex-column flex-sm-row flex-wrap gap-2 justify-content-center mt-4">
                <WhatsAppButton className="btn btn-whatsapp btn-whatsapp-pair" message={WHATSAPP_DEFAULT_MESSAGE}>
                  Chat on WhatsApp
                </WhatsAppButton>
                <Link to={{ pathname: '/', hash: '#portfolio' }} className="btn btn-primary btn-whatsapp-pair">
                  See our work
                </Link>
              </div>
            </div>
      </HeroVideo>
      <div className="container pt-5">
        <div className="row row-cols-1 row-cols-lg-3 g-4">
          {offers.map((offer) => (
            <div className="col" key={offer.title}>
              <article className={`card service-card h-100${offer.leadPackage ? ' service-card-lead' : ''}`}>
                {offer.leadPackage ? (
                  <div className="service-card-lead-badge text-center py-2">Lead package</div>
                ) : null}
                <div className="card-body p-4 d-flex flex-column">
                  <h2 className={`h4${offer.playBadge ? ' service-card-title-badge' : ''}`}>
                    {offer.playBadge ? (
                      <span className="service-play-badge" aria-hidden="true">
                        <svg width="12" height="12" viewBox="0 0 12 12" focusable="false"><path d="M3 1.5v9l7.5-4.5z" fill="currentColor" /></svg>
                      </span>
                    ) : null}
                    {offer.title}
                  </h2>
                  <QuoteLink href={offer.quoteHref} />
                  <p className="small text-secondary">{offer.unit}</p>
                  <p>{offer.description}</p>
                  <ul className="ps-3">{offer.includes.map((item) => <li className="mb-2" key={item}>{item}</li>)}</ul>
                  <p className="small text-secondary">{offer.note}</p>
                  <EmailWhatsAppActions
                    className="contact-actions d-flex flex-column gap-2 mt-auto"
                    mailtoHref={`mailto:rosuepro@gmail.com?subject=${encodeURIComponent(offer.subject)}&body=${encodeURIComponent(offer.body)}`}
                    mailLabel={offer.action}
                    whatsappMessage={offer.whatsappMessage ?? offer.body}
                    buttonClassEmail="btn btn-primary btn-whatsapp-pair w-100"
                    buttonClassWhatsApp="btn btn-whatsapp btn-whatsapp-pair w-100"
                  />
                </div>
              </article>
            </div>
          ))}
        </div>
        <p className="text-center small mt-4 mb-0">
          {GET_QUOTE_LABEL} on WhatsApp or email — we confirm scope and timing personally.
        </p>
        <p className="text-center mt-4">
          <Link to={{ pathname: '/', hash: '#portfolio' }}>View portfolio examples</Link> or <Link to="/about">learn more about RosuePro</Link>.
        </p>
        <div className="service-next-steps mt-4 p-4">
          <h2 className="h4">A simple way to get started</h2>
          <p className="mb-2">
            Choose an offer and describe what you need. We’ll agree on scope and timing before we start.
          </p>
          <EmailWhatsAppActions
            className="contact-actions d-flex flex-column flex-sm-row flex-wrap gap-2 mb-3"
            mailtoHref="mailto:rosuepro@gmail.com?subject=Custom%20development%20enquiry"
            mailLabel="Ask for a custom quote"
            whatsappMessage="Hi RosuePro,\n\nI need a custom quote for website or chatbot work.\nWhat I need: \n"
          />
          <p className="mb-0 small text-secondary">
            Also offering <Link to="/tutoring">Java tutoring for learners</Link> — separate from our business website packages.
          </p>
        </div>
      </div>
    </section>
  );
}
